/**
 * Video Utils
 *
 * Shared helper functions used across video picker modules.
 */

import { VideoRender } from "./video-render";
import { VideoValidate } from "./video-validate";
import type { VideoThumbnailData } from "./video-render";

interface VideoDataLike {
    videoUrl?: string;
    url?: string;
}

interface VideoValidateConfig {
    allowedTypes?: string[];
    maxSize?: number;
    _maxCount?: number;
}

class VideoUtils {
    public static formatDuration(seconds: number): string {
        if (!seconds || Number.isNaN(seconds)) return "0:00";

        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    }

    public static formatFileSize(bytes: number): string {
        if (!bytes || Number.isNaN(bytes)) return "0 B";

        const units = ["B", "KB", "MB", "GB"];
        let size = bytes;
        let unitIndex = 0;

        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024;
            unitIndex++;
        }

        return `${size.toFixed(unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`;
    }

    public static cleanup(videoData: VideoDataLike[]): void {
        if (!Array.isArray(videoData)) return;

        videoData.forEach((video) => {
            if (video.videoUrl) {
                URL.revokeObjectURL(video.videoUrl);
            }
            if (video.url && video.url !== video.videoUrl) {
                URL.revokeObjectURL(video.url);
            }
        });
    }

    public static async processVideos(
        files: FileList | File[],
        config: VideoValidateConfig = {},
    ): Promise<VideoThumbnailData[]> {
        const fileArray = Array.from(files);
        const processedVideos: VideoThumbnailData[] = [];
        const errors: string[] = [];

        for (const file of fileArray) {
            try {
                const validation = VideoValidate.validateVideo(file, config);
                if (!validation.isValid) {
                    errors.push(...validation.errors);
                    continue;
                }

                const videoData = await VideoRender.generateThumbnail(file);
                processedVideos.push(videoData);
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                errors.push(`Failed to process ${file.name}: ${message}`);
            }
        }

        void errors;
        return processedVideos;
    }

    public static generateThumbnail = VideoRender.generateThumbnail;
    public static validateVideo = VideoValidate.validateVideo;
}

export { VideoUtils };
